"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { ArrowLeft, ChevronRight, Menu, X } from "lucide-react";
import { SiteLogo } from "@/components/ui/SiteLogo";

/**
 * Layout reutilizável pras páginas legais (Termos, Privacidade) — VOR-108.
 *
 * Bea (VOR-108): "Ajustar navbar dessas páginas (logo gigante), mover
 * Voltar ao início pro início, renomear pra Voltar, deletar tags LGPD/
 * Termos de Uso, adicionar bullets em seções com listas, painel lateral
 * com TOC. Em mobile, menu hamburger."
 *
 * Estrutura:
 *  - Navbar enxuta: logo Vorseum em 120px + link "Voltar" à direita
 *  - Em desktop (lg+): TOC fixo na coluna esquerda + conteúdo na direita
 *  - Em mobile/tablet: TOC em drawer acionado por menu hamburger na navbar
 *
 * Cada section em `sections` recebe um `slug` (id pra âncora). O TOC
 * navega via âncora e marca a section ativa via IntersectionObserver.
 */

export type LegalSection = {
  /** Id estável pra âncora e TOC. Slug em kebab-case. */
  slug: string;
  /** Título exibido como `<h2>` e item no TOC. */
  title: string;
  /** Conteúdo: cada item vira `<p>` ou `<ul>` conforme tipo. */
  content: LegalBlock[];
};

export type LegalBlock = string | { type: "list"; items: string[] };

export function LegalLayout({
  eyebrow,
  title,
  lastUpdated,
  version,
  intro,
  sections,
  footerNote,
  extraLinks,
}: {
  /** Texto pequeno acima do title. Bea pediu pra remover Eyebrow LGPD/Termos
   * — manter prop opcional caso queiramos voltar; não passar nas páginas. */
  eyebrow?: string;
  title: string;
  lastUpdated: string;
  /** KAN-732 — versão semântica do documento (ex.: `v1.2026-05-12`).
   * Importante pra LGPD: cada attestation/aceite registra a versão vigente,
   * então mudar o ToS exige subir a versão. Ver docs/legal/tos-versioning.md. */
  version?: string;
  intro?: string;
  sections: LegalSection[];
  /** Texto no final, antes dos links de saída. */
  footerNote?: string;
  /** Links extras na footer (ex.: Termos→Privacidade). */
  extraLinks?: Array<{ href: string; label: string }>;
}) {
  // VOR-126: drawer mobile estado mantido aqui (fora do header). O header
  // tem `backdrop-blur-xl`, que cria containing block pra elementos
  // position:fixed descendentes — se o drawer fosse renderizado dentro do
  // header, ficaria preso à faixa do header em vez de cobrir o viewport.
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="relative isolate min-h-screen">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[480px] bg-grid-radial opacity-50 blur-3xl" />

      <LegalHeader onOpenDrawer={() => setDrawerOpen(true)} />

      {drawerOpen ? (
        <MobileTocDrawer sections={sections} onClose={() => setDrawerOpen(false)} />
      ) : null}

      <main className="py-12 sm:py-16 lg:py-20">
        <div className="shell">
          <div className="grid gap-12 lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-12 xl:grid-cols-[18rem_minmax(0,1fr)]">
            <LegalSidebar sections={sections} />

            <article className="min-w-0 max-w-3xl">
              <Link
                href="/"
                className="inline-flex items-center gap-2 text-sm text-mist transition hover:text-white"
              >
                <ArrowLeft size={16} aria-hidden="true" />
                Voltar
              </Link>

              {eyebrow ? (
                <p className="mt-8 text-xs font-semibold uppercase tracking-[0.18em] text-accent">
                  {eyebrow}
                </p>
              ) : null}

              <h1
                className={`font-display text-4xl font-medium text-white sm:text-5xl ${
                  eyebrow ? "mt-4" : "mt-8"
                }`}
              >
                {title}
              </h1>
              <p className="mt-5 text-base leading-8 text-mist sm:text-lg">
                Última atualização: {lastUpdated}
                {version ? (
                  <span className="ml-2 inline-flex items-center rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-xs font-mono text-mist">
                    {version}
                  </span>
                ) : null}
              </p>

              {intro ? (
                <p className="mt-6 text-base leading-8 text-mist">{intro}</p>
              ) : null}

              <div className="mt-12 space-y-12">
                {sections.map((section) => (
                  <section
                    key={section.slug}
                    id={section.slug}
                    aria-labelledby={`${section.slug}-heading`}
                    className="scroll-mt-24"
                  >
                    <h2
                      id={`${section.slug}-heading`}
                      className="font-display text-xl font-medium text-white sm:text-2xl"
                    >
                      {section.title}
                    </h2>
                    <div className="mt-4 space-y-4">
                      {section.content.map((block, idx) =>
                        typeof block === "string" ? (
                          <p key={idx} className="text-base leading-8 text-mist">
                            {block}
                          </p>
                        ) : (
                          <ul
                            key={idx}
                            className="ml-5 list-disc space-y-2 text-base leading-8 text-mist marker:text-accent"
                          >
                            {block.items.map((item) => (
                              <li key={item} className="pl-1">
                                {item}
                              </li>
                            ))}
                          </ul>
                        )
                      )}
                    </div>
                  </section>
                ))}
              </div>

              {(footerNote || extraLinks) && (
                <div className="mt-16 border-t border-white/10 pt-10">
                  {footerNote ? (
                    <p className="text-sm text-mist">{footerNote}</p>
                  ) : null}
                  <div className="mt-6 flex flex-wrap gap-6">
                    {extraLinks?.map((l) => (
                      <Link
                        key={l.href}
                        href={l.href}
                        className="inline-flex items-center gap-2 text-sm text-accent transition hover:text-white"
                      >
                        {l.label}
                      </Link>
                    ))}
                    <Link
                      href="/"
                      className="inline-flex items-center gap-2 text-sm text-accent transition hover:text-white"
                    >
                      <ArrowLeft size={14} aria-hidden="true" />
                      Voltar
                    </Link>
                  </div>
                </div>
              )}
            </article>
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * Header da página legal: logo Vorseum em 120px + botão hamburger mobile.
 * Em desktop, hamburger fica oculto (TOC fixo na sidebar).
 *
 * VOR-126: o estado do drawer é mantido no LegalLayout (pai) pra que o
 * drawer seja renderizado fora deste header. O header tem
 * `backdrop-blur-xl`, e `backdrop-filter` cria containing block pra
 * elementos `position: fixed`, o que prendia o drawer dentro da faixa
 * do header.
 */
function LegalHeader({ onOpenDrawer }: { onOpenDrawer: () => void }) {
  return (
    <header className="border-b border-white/10 bg-canvas/70 backdrop-blur-xl">
      <div className="shell flex min-h-16 items-center justify-between gap-4">
        <SiteLogo priority width={120} />
        <button
          type="button"
          onClick={onOpenDrawer}
          aria-label="Abrir índice da página"
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-sm text-mist transition hover:border-white/20 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas lg:hidden"
        >
          <Menu size={16} aria-hidden="true" />
          Índice
        </button>
      </div>
    </header>
  );
}

/**
 * Sidebar fixa com TOC em desktop (lg+). Esconde em mobile/tablet.
 * Marca a section ativa via IntersectionObserver pra UX clara enquanto
 * o usuário scrolla.
 */
function LegalSidebar({ sections }: { sections: LegalSection[] }) {
  const activeSlug = useActiveSection(sections.map((s) => s.slug));

  return (
    <aside className="hidden lg:block" aria-label="Índice da página">
      <nav className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto pr-2">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-mist/70">
          Nesta página
        </p>
        <ul className="mt-4 space-y-1">
          {sections.map((s) => {
            const isActive = s.slug === activeSlug;
            return (
              <li key={s.slug}>
                <Link
                  href={`#${s.slug}`}
                  aria-current={isActive ? "true" : undefined}
                  className={`block rounded-md border-l-2 px-3 py-1.5 text-sm transition ${
                    isActive
                      ? "border-accent bg-white/5 text-white"
                      : "border-white/10 text-mist hover:border-white/30 hover:text-white"
                  }`}
                >
                  {s.title}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}

/**
 * Drawer mobile com a mesma lista de seções do TOC desktop.
 */
function MobileTocDrawer({
  sections,
  onClose,
}: {
  sections: LegalSection[];
  onClose: () => void;
}) {
  // Fecha com ESC e bloqueia scroll do body enquanto aberto.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Índice da página"
      className="fixed inset-0 z-50 flex flex-col bg-canvas/95 backdrop-blur-md lg:hidden"
    >
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-mist/70">
          Nesta página
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar índice"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-mist transition hover:border-white/20 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
      <ul className="flex-1 overflow-y-auto px-4 py-4">
        {sections.map((s) => (
          <li key={s.slug}>
            <Link
              href={`#${s.slug}`}
              onClick={onClose}
              className="flex items-center justify-between gap-3 rounded-lg px-3 py-3 text-sm text-mist transition hover:bg-white/5 hover:text-white"
            >
              <span>{s.title}</span>
              <ChevronRight size={14} aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Hook que retorna o slug da section atualmente em vista, observando
 * cruzamentos com o topo do viewport. Usa um rootMargin que prioriza
 * a section cuja headline acabou de cruzar o topo (offset ~120px).
 */
function useActiveSection(slugs: string[]): string | null {
  const [active, setActive] = useState<string | null>(slugs[0] ?? null);

  useEffect(() => {
    if (typeof window === "undefined" || slugs.length === 0) return;

    const elements = slugs
      .map((slug) => document.getElementById(slug))
      .filter((el): el is HTMLElement => Boolean(el));

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActive(entry.target.id);
            break;
          }
        }
      },
      {
        // Janela "viva" — section ativa quando a headline está nos
        // 25% superiores do viewport.
        rootMargin: "-120px 0px -70% 0px",
        threshold: 0,
      }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [slugs]);

  return active;
}
